/**
 * Palette, theme, density and font scale.
 *
 * PER BROWSER PROFILE, NOT PER ACCOUNT, and `colour.md` is explicit about why:
 * a shared family laptop must not repaint when somebody switches account. So
 * these four live in localStorage and never travel with the mailbox. The only
 * appearance value that follows the account is the per-account accent channel,
 * which touches chrome and is not one of these.
 *
 * EVERY READ IS GUARDED. localStorage throws outright in some contexts — a
 * browser set to block site data, a private window, an embedded webview — and
 * an appearance preference is not worth taking the application down for. The
 * fallback in each case is the documented default, which is what a first visit
 * gets anyway.
 */
import { DEFAULT_PALETTE, paletteNames } from '@hearth/tokens'

export type Density = 'compact' | 'cozy' | 'comfortable'
/** `system` stamps NOTHING, so `prefers-color-scheme` decides. */
export type Theme = 'light' | 'dark' | 'system'

/**
 * How many messages the list loads at a time.
 *
 * IT MEANS WHAT IT SAYS. Set it to 100 and the list holds 100 and stops; there
 * is a "Load more" at the bottom for the next 100. An earlier version treated
 * this as a fetch CHUNK and kept loading as you scrolled, which is a different
 * feature wearing the same label — "I set the paging to 100 but it keeps on
 * fetching" is exactly the report that produced this comment.
 *
 * ANY NUMBER, not a fixed ladder, because the right one depends on the screen
 * and the archive rather than on anything this code knows. Bounded only where
 * the bounds are real: below `MIN_PAGE_SIZE` a page is not worth a round trip,
 * and above `MAX_PAGE_SIZE` a browser holding the rows starts to matter more
 * than the scrolling does.
 *
 * The API clamps `limit` to 200 per request, so a page larger than that is
 * several requests walking one cursor — which the client does for itself.
 */
export type PageSize = number

export const MIN_PAGE_SIZE = 10
export const MAX_PAGE_SIZE = 1000

export const clampPageSize = (value: number): number =>
  Math.min(MAX_PAGE_SIZE, Math.max(MIN_PAGE_SIZE, Math.round(value)))

export interface Appearance {
  readonly palette: string
  readonly theme: Theme
  readonly density: Density
  /** 1..7 — the seven steps of `--fs`, XS to XXXL. */
  readonly fontScale: number
  readonly pageSize: PageSize
}

export const DEFAULT_APPEARANCE: Appearance = {
  palette: DEFAULT_PALETTE,
  theme: 'system',
  density: 'cozy',
  fontScale: 4,
  pageSize: 100,
}

const KEY = 'hearth.appearance'

function read(): Appearance {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return DEFAULT_APPEARANCE
    const stored = JSON.parse(raw) as Partial<Appearance>
    return {
      // VALIDATED, NOT TRUSTED. This is the user's own storage, but it survives
      // upgrades: a palette that was removed, or a density spelling that
      // changed, would otherwise stamp an attribute no stylesheet matches and
      // leave the shell with no colours at all.
      palette: typeof stored.palette === 'string' && paletteNames.includes(stored.palette)
        ? stored.palette
        : DEFAULT_APPEARANCE.palette,
      theme: stored.theme === 'light' || stored.theme === 'dark' ? stored.theme : 'system',
      density:
        stored.density === 'compact' || stored.density === 'comfortable' || stored.density === 'cozy'
          ? stored.density
          : DEFAULT_APPEARANCE.density,
      fontScale:
        typeof stored.fontScale === 'number' && stored.fontScale >= 1 && stored.fontScale <= 7
          ? Math.round(stored.fontScale)
          : DEFAULT_APPEARANCE.fontScale,
      pageSize:
        typeof stored.pageSize === 'number' && Number.isFinite(stored.pageSize)
          ? clampPageSize(stored.pageSize)
          : DEFAULT_APPEARANCE.pageSize,
    }
  } catch {
    return DEFAULT_APPEARANCE
  }
}

/**
 * Stamp the four attributes the stylesheet keys off.
 *
 * `pageSize` IS DELIBERATELY NOT AMONG THEM. It changes what the client asks
 * the server for, not how anything looks, so it has no attribute and no CSS —
 * it lives here only because it is the same kind of thing: a per-browser
 * preference, not per account, so a shared family laptop does not change how
 * somebody else's list loads.
 */
export function apply(appearance: Appearance, root: HTMLElement = document.documentElement): void {
  root.dataset.palette = appearance.palette
  // `system` REMOVES the attribute rather than setting a value, because the
  // dark rules are guarded as `:not([data-theme="light"])` inside the media
  // query — the absence is what lets the OS decide.
  if (appearance.theme === 'system') delete root.dataset.theme
  else root.dataset.theme = appearance.theme
  root.dataset.density = appearance.density
  root.dataset.fs = String(appearance.fontScale)
}

export function applyStoredAppearance(): Appearance {
  const appearance = read()
  apply(appearance)
  return appearance
}

export function save(appearance: Appearance): void {
  apply(appearance)
  try {
    localStorage.setItem(KEY, JSON.stringify(appearance))
  } catch {
    // A preference that cannot be remembered is still a preference that works
    // for this session. Failing the write must not undo the apply above.
  }
}

export { read as readAppearance }
